import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import ApiCalls from '../pods/auth/api/ApiCalls';
import { cookieService } from './CookieService';

import config from '../config';
import ApiErrorResponse from 'src/pods/system/models/ApiErrorResponse';

export interface IInvokeOptions {
    noAuthentication?: boolean;
    background?: boolean;
    headers?: any;
}

export default class AjaxService {
    private accessToken?: string | null;

    constructor() {
        this.init();
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        axios.interceptors.response.use(
            (response) => {
                return response.data;
            },
            async function (error) {
                const originalRequest: any = error.config;

                if (error?.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;
                    const cookieData = cookieService.getCookie();

                    const newCredentials = await ApiCalls.refreshToken({
                        payload: { token: cookieData?.refreshToken },
                    });

                    axios.defaults.headers.common['Content-Type'] = 'application/json';
                    axios.defaults.headers.common['Authorization'] = 'Bearer ' + newCredentials.data.accessToken;

                    originalRequest.headers['Content-Type'] = 'application/json';
                    originalRequest.headers['Authorization'] = 'Bearer ' + newCredentials.data.accessToken;

                    const newCookieData = {
                        accessToken: newCredentials?.data?.accessToken,
                        refreshToken: newCredentials?.data?.refreshToken,
                    };

                    self.setAuthToken(newCredentials?.data?.accessToken);
                    await cookieService.setCookie(JSON.stringify(newCookieData), 30);

                    return axios(originalRequest);
                }

                if (error?.response?.data?.error?.code === 400410) {
                    cookieService.removeCookie();
                    return window.location.assign(`${config.SSO_APP_URL}/login`);
                }

                return Promise.reject(error);
            },
        );
    }

    init(): void {
        const cookieData = cookieService.getCookie();
        this.accessToken = cookieData?.accessToken;
    }

    setAuthToken(newAuthToken: string | null): void {
        this.accessToken = newAuthToken;
    }

    public get endpoint(): string {
        return this.basePath;
    }

    private get basePath(): string {
        return config.API_BASE_URL;
    }

    private get defaultHeaders() {
        const cookieData = cookieService.getCookie();
        const token = cookieData?.accessToken;

        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };
    }

    // eslint-disable-next-line
    private invoke<T>(method: string, url: string, data?: any, options?: IInvokeOptions) {
        let defaultHeaders = this.defaultHeaders;

        if (options && options.noAuthentication) {
            delete defaultHeaders['Authorization'];
        }

        if (options && options.headers) {
            defaultHeaders = { ...defaultHeaders, ...options.headers };
        }

        return axios({
            method,
            url: `${this.basePath}${url}`,
            headers: defaultHeaders,
            data,
        } as AxiosRequestConfig)
            .then((response: AxiosResponse<T>) => {
                return response as any;
            })
            .catch(async (error: AxiosError) => {
                if (error.response) {
                    const status = error.response.status;
                    return Promise.reject(new ApiErrorResponse(error.response.data?.error, status));
                }
                const { message } = JSON.parse(JSON.stringify(error));
                return Promise.reject(new ApiErrorResponse({ message }, 0));
            });
    }

    // eslint-disable-next-line
    public get<T = any>(url: string, options?: IInvokeOptions): Promise<T> {
        return this.invoke<T>('GET', url, null, options);
    }

    // eslint-disable-next-line
    public post<T = any>(url: string, data: any = null, options?: IInvokeOptions): Promise<T> {
        return this.invoke<T>('POST', url, data, options);
    }

    // eslint-disable-next-line
    public put<T = any>(url: string, data?: any, options?: IInvokeOptions): Promise<T> {
        return this.invoke<T>('PUT', url, data, options);
    }

    // eslint-disable-next-line
    public patch<T = any>(url: string, data?: any, options?: IInvokeOptions): Promise<T> {
        return this.invoke<T>('PATCH', url, data, options);
    }

    // eslint-disable-next-line
    public delete<T = any>(url: string, options?: IInvokeOptions): Promise<T> {
        return this.invoke('DELETE', url, null, options);
    }
}

export const ajaxService = new AjaxService();
